import React from "react";
import { useSelector, useDispatch } from "react-redux";
import QuantitySelector from "../../assets/QuantitySelector";
import { useState } from "react";
import { addProduct } from "../../actions/basket";
import { useModal } from "../../assets/useModal";
import { totalLineItem } from "../../helpers";

function serializeChoices(choices) {
	let result = [];
	Object.entries(choices).forEach(([name, options]) => {
		options.forEach(({ addOn, quantity }) => {
			result.push({ name, addOn, quantity });
		});
	});
	return result;
}

export default function ProductConfigurer({ product, featureColor, onCancel }) {

	const dispatch = useDispatch();
	const [choices, setChoices] = useState({});
	const [itemQuantity, setItemQuantity] = useState(1);
	const { closeModal } = useModal();

	const { addOns } = useSelector(state => ({ addOns: state.vendor.addOns }));

	function setChoice(option, addOn, quantity = 1) {
		setChoices({
			...choices,
			[option.name]: (option.maxCount === 1) ?
				[{ addOn: addOn.id, quantity }]
				:
				[
					...(choices[option.name] || []).filter(choice => choice.addOn !== addOn.id),
					...(quantity ? [{ addOn: addOn.id, quantity }] : [])
				]
		})
	}

	function getChoices(option) {
		return choices[option.name] || [];
	}

	// Create the serialized set of choices for use later.
	const serializedChoices = serializeChoices(choices);

	// Total the price of one of the items.
	const itemTotal = totalLineItem({
		quantity: 1,
		options: serializedChoices,
		product
	}, { addOns });

	// Get the options and pair up with their addOns.
	const options = (product.options || []).map(option => ({
		...option,
		addOns: option.addOns
			.map(optionAddOn => ({
				...optionAddOn,
				addOn: addOns
					.find(addOn => addOn.id === optionAddOn.addOn)
			}))
			.filter(optionAddOn => optionAddOn.addOn)
	}));

	// Checks whether all required options are met.
	const canComplete = (function () {
		for (let i = 0; i < options.length; i++) {
			let option = options[i];
			let totalSelected = getChoices(option).reduce((acc, choice) => acc + choice.quantity, 0);
			// First handle option caps.
			if (option.minCount && totalSelected < option.minCount) return false;
			if (option.maxCount && totalSelected > option.maxCount) return false;
			// Then the addOn ones.
			for (let j = 0; j < option.addOns.length; j++) {
				let { quantity, maxCount, minCount } = option.addOns[j];
				if (minCount && quantity < minCount) return false;
				if (maxCount && quantity > maxCount) return false;
			}
		}
		return true;
	})();

	return <div className="productConfigurer" style={{ minHeight: 300 }}>

		<div className="product-text padded2">
			<h4 className="featureFont">{product.name}{product.parent ? product.parent.id : null}</h4>
			{Boolean(product.description) && <>
				<br />
				<div>{product.description}</div>
			</>}
		</div>
		<div className="options">
			{options.map(option => {

				// Reference to whether we can select single addOns, or multiple.
				const isSingle = (option.maxCount === 1);

				return <div className="option" key={option.id}>
					<div className="option-header padded2 featureFont v-middle">
						{/* The title of the option */}
						<b>{option.name}</b>
						{/* Guidance for how many to select */}
						<b
							className="typeGuidance text-right"
							style={{ color: "#aaaaaa" }}
						>
							{isSingle ?
								<span>Pick 1</span>
								:
								Boolean(option.maxCount) ?
									<span>Pick up to {option.maxCount}</span>
									:
									<span>Pick multiple</span>
							}
						</b>
						{Boolean(option.minCount) && <b className="requirement brand-c">{option.minCount} required</b>}
					</div>
					<div className="option-choices">
						{option.addOns.map(({ id: optionAddOnId, addOn, maxCount: addOnMaxRaw }) => {

							// Find the current quantity selected for this addOn.
							const addOnQuantity = (getChoices(option)
								.find(choice => choice.addOn === addOn.id) || { quantity: 0 }).quantity;
							// Calculate the maximum allowed based on current selection and option rules.
							const max = (() => {

								// If we're on single mode, it's easy.
								if (isSingle) return 1;

								// Make sure zeros are treated as infinity for max values.
								const addOnMax = [null, 0].includes(addOnMaxRaw) ? Infinity : addOnMaxRaw;
								const optionMax = [null, 0].includes(option.maxCount) ? Infinity : option.maxCount;

								// We need to compare to used values. 
								// First get the total selected currently.
								let totalQuantitySelected = getChoices(option)
									.filter(({ addOn: a }) => a !== addOn.id)
									.reduce((acc, choice) => acc + choice.quantity, 0);

								// Now compare whether option or addon max is more restrictive.
								// And use whichever is.
								let maxFromQuantitySelected = optionMax - totalQuantitySelected;
								if (addOnMax < maxFromQuantitySelected) {
									return addOnMax;
								} else {
									return maxFromQuantitySelected;
								}
							})();

							return <div className="option-addOn padded2 bordered-top" key={optionAddOnId}>
								<div className="flexy featureFont">
									{/* The addOn's name. */}
									<div style={{ flex: 2 }}>
										<span className="name" >
											<b>{addOn.name}</b>
										</span>
										{/* Addon's price, if applicable. */}
										{Boolean(addOn.price) && <span className="price text-right" style={{ color: featureColor }}>
											<b>+£{(addOn.price / 100).toFixed(2)}</b>
										</span>}
									</div>
									<div className="addOn-controls text-right">
										<QuantitySelector
											value={addOnQuantity}
											onChange={value => {
												setChoice(option, addOn, value);
											}}
											max={max}
											forceNumerical={!isSingle}
											showWhenDisabled={true}
										/>
									</div>
								</div>
								{/* Description, if present */}
								{Boolean(addOn.description) && <div className="description">
									{addOn.description}
								</div>}
							</div>
						})}
					</div>
				</div>
			})}
		</div>
		{/* Quick selector for how many you want. */}
		<div className="amount padded2 text-center bordered-top">
			<div className="price featureFont">
				{itemQuantity === 1 && <span>Just one?</span>}
				<span style={itemQuantity === 1 ? { color: featureColor } : null}>
					£{(itemTotal / 100).toFixed(2)}
				</span>
			</div>
			<QuantitySelector
				min={1}
				value={itemQuantity}
				onChange={value => setItemQuantity(value)}
				showWhenDisabled={true}
			/>
		</div>
		{/* Controls for accepting choice or cancelling */}
		<div className="flexy">
			<button
				className="chunky"
				onClick={onCancel}
			>Cancel</button>
			<button
				className="chunky"
				disabled={!canComplete}
				onClick={e => {
					if (!canComplete) return;
					dispatch(addProduct(
						product,
						itemQuantity,
						serializedChoices
					));
					closeModal();
				}}
			>All done</button>
		</div>
	</div>
}